import { create } from 'zustand';
import { supabase } from '../supabase';
import { eventBus } from '../events/eventBus';
import type {
  BookingEventType,
  BookingEventTypeFormData,
  BookingAvailability,
  BookingAvailabilityFormData,
  BookingDateOverride,
  BookingDateOverrideFormData,
  Booking,
  BookingSettings,
  BookingSlot,
  BookingCreatePublicRequest,
  BookingCreateInternalFormData,
  PublicBookingPageData,
  PublicEventTypeData,
} from '../types/booking';

// ─── Helper: estrai il body JSON da un errore di supabase.functions.invoke ───
// Le Edge Function che rispondono con status non-2xx (es. 409 slot_taken) vengono
// esposte da supabase-js come FunctionsHttpError con il body nel Response `error.context`.
async function parseFunctionsError(error: any): Promise<any | null> {
  try {
    const ctx = error?.context;
    if (ctx && typeof ctx.json === 'function') {
      return await ctx.clone().json();
    }
    if (ctx && typeof ctx.text === 'function') {
      const t = await ctx.clone().text();
      try { return JSON.parse(t); } catch { return t ? { message: t } : null; }
    }
  } catch (_) { /* body non leggibile */ }
  return null;
}

// Messaggi per chi prenota dalla pagina pubblica (booking-create). Prima la pagina mostrava
// "Edge Function returned a non-2xx status code" anche quando l'orario era solo scaduto
// (preavviso minimo superato tra l'apertura della pagina e la conferma, caso ZIA 02/10/2026).
const PUBLIC_BOOKING_ERRORS: Record<string, string> = {
  too_late: "Questo orario non è più prenotabile perché è troppo vicino. Scegli un altro orario.",
  slot_taken: "Questo orario è appena stato prenotato da qualcun altro. Scegli un altro orario.",
  day_full: 'Per questo giorno non ci sono più posti. Scegli un altro giorno.',
  week_full: 'Per questa settimana non ci sono più posti. Scegli un altro giorno.',
  month_full: 'Per questo mese non ci sono più posti. Scegli un altro giorno.',
};

function mapPublicBookingError(body: any): string {
  if (body && typeof body === 'object') {
    if (body.error && PUBLIC_BOOKING_ERRORS[body.error]) return PUBLIC_BOOKING_ERRORS[body.error];
    if (body.error === 'invalid_invite' && body.message) return body.message;
    if (body.error === 'validation_error') {
      return /email/i.test(String(body.message)) ? "Controlla l'indirizzo email." : 'Controlla i dati inseriti e riprova.';
    }
    if (typeof body.message === 'string' && /[àèéìòù]|\b(il|la|non|è)\b/i.test(body.message)) return body.message;
  }
  return 'Non è stato possibile confermare la prenotazione. Riprova tra qualche istante.';
}

// Traduce il codice errore di booking-manage in un messaggio leggibile in italiano.
function mapBookingError(body: any): string {
  if (!body) return 'Operazione non riuscita';
  if (typeof body === 'string') return body;
  if (body.error === 'slot_taken') {
    return "L'orario selezionato è già occupato da un altro appuntamento. Scegli un orario libero.";
  }
  return body.message || body.error || 'Operazione non riuscita';
}

// ─── Helper: get user org data ───────────────────────────────

async function getUserData() {
  const { data: userData } = await supabase.auth.getUser();
  if (!userData.user) throw new Error('User not authenticated');

  const { data: profile } = await supabase
    .from('user_profiles')
    .select('organization_id, booking_slug')
    .eq('id', userData.user.id)
    .single();

  if (!profile?.organization_id) throw new Error('Organization not found');

  return {
    userId: userData.user.id,
    organizationId: profile.organization_id,
    bookingSlug: profile.booking_slug as string | null,
  };
}

// ─── Store Interface ─────────────────────────────────────────

interface BookingStore {
  // Event Types
  eventTypes: BookingEventType[];
  eventTypesLoading: boolean;
  eventTypesError: string | null;

  // Availability
  availability: BookingAvailability[];
  dateOverrides: BookingDateOverride[];
  availabilityLoading: boolean;

  // Bookings
  bookings: Booking[];
  bookingsLoading: boolean;
  bookingsError: string | null;

  // Settings
  bookingSettings: BookingSettings | null;
  settingsLoading: boolean;

  // User booking slug
  userBookingSlug: string | null;

  // Public flow state
  publicOrgData: PublicBookingPageData | null;
  publicEventTypeData: PublicEventTypeData | null;
  publicSlots: BookingSlot[];
  publicAllSlots: BookingSlot[];
  publicBusyIntervals: Array<{ start: string; end: string }>;
  publicDayBookings: Array<{ start: string; end: string; booker_name: string; event_type_title: string; event_type_color: string }>;
  publicSlotsLoading: boolean;
  publicBookingResult: any | null;
  publicBookingCreating: boolean;
  publicError: string | null;

  // ── Event Type actions ──
  fetchEventTypes: () => Promise<void>;
  createEventType: (data: BookingEventTypeFormData) => Promise<BookingEventType | null>;
  updateEventType: (id: string, data: Partial<BookingEventTypeFormData>) => Promise<void>;
  deleteEventType: (id: string) => Promise<void>;
  duplicateEventType: (id: string) => Promise<string | null>;
  toggleEventTypeActive: (id: string, active: boolean) => Promise<void>;

  // ── Availability actions ──
  fetchAvailability: (eventTypeId?: string) => Promise<void>;
  saveAvailability: (rules: BookingAvailabilityFormData[], eventTypeId?: string, targetUserId?: string) => Promise<void>;
  fetchDateOverrides: (eventTypeId?: string) => Promise<void>;
  createDateOverride: (data: BookingDateOverrideFormData) => Promise<void>;
  deleteDateOverride: (id: string) => Promise<void>;

  // ── Booking actions ──
  fetchBookings: (filters?: { status?: string; from?: string; to?: string; filterByUserId?: string }) => Promise<void>;
  fetchBookingById: (id: string) => Promise<Booking | null>;
  cancelBooking: (id: string, reason?: string) => Promise<boolean>;
  rescheduleBooking: (id: string, newStartTime: string, newEndTime?: string, forceOverride?: boolean) => Promise<{ success: boolean; error?: string }>;
  completeBooking: (id: string) => Promise<boolean>;
  markNoShow: (id: string) => Promise<boolean>;
  confirmBooking: (id: string) => Promise<boolean>;
  rejectBooking: (id: string, reason?: string) => Promise<boolean>;
  updateBookingNotes: (id: string, notes: string) => Promise<boolean>;
  createInternalBooking: (data: BookingCreateInternalFormData) => Promise<Booking | null>;

  // ── Settings actions ──
  fetchBookingSettings: () => Promise<void>;
  saveBookingSettings: (data: Partial<BookingSettings>) => Promise<void>;

  // ── User slug actions ──
  fetchUserBookingSlug: () => Promise<void>;
  saveUserBookingSlug: (slug: string) => Promise<boolean>;

  // ── Public actions (no auth) ──
  fetchPublicPage: (userSlug: string) => Promise<void>;
  fetchPublicEventType: (userSlug: string, eventTypeSlug: string) => Promise<void>;
  fetchPublicSlots: (eventTypeId: string, date: string, timezone: string, userId?: string) => Promise<void>;
  createPublicBooking: (userSlug: string, eventTypeSlug: string, data: BookingCreatePublicRequest) => Promise<any | null>;
  resetPublicFlow: () => void;

  // ── Public self-service (manage via public_token) ──
  publicManagedBooking: any | null;
  publicManagedLoading: boolean;
  publicManagedError: string | null;
  publicManagedSuccess: string | null;
  fetchPublicBooking: (publicToken: string) => Promise<void>;
  cancelPublicBooking: (publicToken: string, reason?: string) => Promise<boolean>;
  reschedulePublicBooking: (publicToken: string, newStartTime: string) => Promise<boolean>;
  resetPublicManaged: () => void;
}

// ─── Store Implementation ────────────────────────────────────

export const useBookingStore = create<BookingStore>((set, get) => ({
  // State
  eventTypes: [],
  eventTypesLoading: false,
  eventTypesError: null,
  availability: [],
  dateOverrides: [],
  availabilityLoading: false,
  bookings: [],
  bookingsLoading: false,
  bookingsError: null,
  bookingSettings: null,
  settingsLoading: false,
  userBookingSlug: null,
  publicOrgData: null,
  publicEventTypeData: null,
  publicSlots: [],
  publicAllSlots: [],
  publicBusyIntervals: [],
  publicDayBookings: [],
  publicSlotsLoading: false,
  publicBookingResult: null,
  publicBookingCreating: false,
  publicError: null,
  publicManagedBooking: null,
  publicManagedLoading: false,
  publicManagedError: null,
  publicManagedSuccess: null,

  // ══════════════════════════════════════════════════════════
  // EVENT TYPES
  // ══════════════════════════════════════════════════════════

  fetchEventTypes: async () => {
    set({ eventTypesLoading: true, eventTypesError: null });
    try {
      const { organizationId } = await getUserData();

      const { data, error } = await supabase
        .from('booking_event_types')
        .select(`
          *,
          assigned_user:user_profiles!booking_event_types_assigned_user_id_fkey(id, email, full_name, avatar_url, booking_slug),
          assignments:booking_event_type_assignments(user_id, user:user_profiles(id, email, full_name, avatar_url, booking_slug))
        `)
        .eq('organization_id', organizationId)
        .order('created_at', { ascending: true });

      if (error) throw error;

      // Normalize: extract assigned_users from junction table
      const normalized = (data || []).map((et: any) => ({
        ...et,
        assigned_users: (et.assignments || []).map((a: any) => a.user).filter(Boolean),
      }));
      set({ eventTypes: normalized, eventTypesLoading: false });
    } catch (err: any) {
      set({ eventTypesError: err.message, eventTypesLoading: false });
    }
  },

  createEventType: async (data) => {
    try {
      const { userId, organizationId } = await getUserData();

      const assignedUserIds = data.assigned_user_ids && data.assigned_user_ids.length > 0
        ? data.assigned_user_ids
        : [data.assigned_user_id || userId];

      // assignment_mode e availability_scope sono campi solo di interfaccia
      // (EventTypeEditPage): la tabella non li ha e PostgREST risponde 400
      // PGRST204 se arrivano nel payload (visto in collaudo il 02/09/2026).
      const { assigned_user_ids: _, assignment_mode: _am, availability_scope: _as, ...insertData } = data as any;

      const { data: created, error } = await supabase
        .from('booking_event_types')
        .insert({
          ...insertData,
          organization_id: organizationId,
          assigned_user_id: assignedUserIds[0],
          created_by: userId,
        })
        .select()
        .single();

      if (error) throw error;

      // Insert junction table rows
      if (created) {
        const rows = assignedUserIds.map(uid => ({
          event_type_id: created.id,
          user_id: uid,
          organization_id: organizationId,
        }));
        await supabase.from('booking_event_type_assignments').insert(rows);
      }

      await get().fetchEventTypes();
      return created;
    } catch (err: any) {
      set({ eventTypesError: err.message });
      return null;
    }
  },

  updateEventType: async (id, data) => {
    try {
      const { organizationId } = await getUserData();

      // Stessa pulizia di createEventType: assignment_mode e availability_scope
      // non esistono in tabella, PostgREST risponderebbe 400 PGRST204.
      const { assigned_user_ids, assignment_mode: _am, availability_scope: _as, ...updateData } =
        data as BookingEventTypeFormData & { assigned_user_ids?: string[]; assignment_mode?: unknown; availability_scope?: unknown };

      // If assigned_user_ids provided, update assigned_user_id to first (backward compat)
      if (assigned_user_ids && assigned_user_ids.length > 0) {
        (updateData as any).assigned_user_id = assigned_user_ids[0];
      }

      const { error } = await supabase
        .from('booking_event_types')
        .update(updateData)
        .eq('id', id)
        .eq('organization_id', organizationId);

      if (error) throw error;

      // Sync junction table if assigned_user_ids provided
      if (assigned_user_ids) {
        await supabase
          .from('booking_event_type_assignments')
          .delete()
          .eq('event_type_id', id);

        if (assigned_user_ids.length > 0) {
          const rows = assigned_user_ids.map(uid => ({
            event_type_id: id,
            user_id: uid,
            organization_id: organizationId,
          }));
          await supabase.from('booking_event_type_assignments').insert(rows);
        }
      }

      await get().fetchEventTypes();
    } catch (err: any) {
      set({ eventTypesError: err.message });
    }
  },

  deleteEventType: async (id) => {
    try {
      const { organizationId } = await getUserData();
      const { error } = await supabase
        .from('booking_event_types')
        .delete()
        .eq('id', id)
        .eq('organization_id', organizationId);

      if (error) throw error;
      set(state => ({ eventTypes: state.eventTypes.filter(et => et.id !== id) }));
    } catch (err: any) {
      set({ eventTypesError: err.message });
    }
  },

  duplicateEventType: async (id) => {
    try {
      const { userId, organizationId } = await getUserData();

      // Load original event type
      const { data: original, error: fetchErr } = await supabase
        .from('booking_event_types')
        .select('*')
        .eq('id', id)
        .eq('organization_id', organizationId)
        .single();
      if (fetchErr || !original) throw fetchErr || new Error('Event type not found');

      // Load assignments
      const { data: assignments } = await supabase
        .from('booking_event_type_assignments')
        .select('user_id')
        .eq('event_type_id', id);

      // Load availability
      const { data: availRules } = await supabase
        .from('booking_availability')
        .select('user_id, day_of_week, start_time, end_time, is_active, timezone')
        .eq('event_type_id', id)
        .eq('organization_id', organizationId);

      // Create duplicate
      const { id: _id, created_at, updated_at, created_by, ...rest } = original;
      const { data: created, error: createErr } = await supabase
        .from('booking_event_types')
        .insert({
          ...rest,
          title: `${original.title} (copia)`,
          slug: `${original.slug}-copia`,
          created_by: userId,
        })
        .select()
        .single();
      if (createErr || !created) throw createErr || new Error('Failed to duplicate');

      // Copy assignments
      const assignmentUserIds = (assignments || []).map(a => a.user_id);
      if (assignmentUserIds.length > 0) {
        await supabase.from('booking_event_type_assignments').insert(
          assignmentUserIds.map(uid => ({ event_type_id: created.id, user_id: uid, organization_id: organizationId }))
        );
      }

      // Copy availability
      if (availRules && availRules.length > 0) {
        await supabase.from('booking_availability').insert(
          availRules.map(r => ({
            organization_id: organizationId,
            user_id: r.user_id,
            event_type_id: created.id,
            day_of_week: r.day_of_week,
            start_time: r.start_time,
            end_time: r.end_time,
            is_active: r.is_active,
            timezone: r.timezone,
          }))
        );
      }

      await get().fetchEventTypes();
      return created.id as string;
    } catch (err: any) {
      set({ eventTypesError: err.message });
      return null;
    }
  },

  toggleEventTypeActive: async (id, active) => {
    try {
      const { organizationId } = await getUserData();
      await supabase
        .from('booking_event_types')
        .update({ is_active: active })
        .eq('id', id)
        .eq('organization_id', organizationId);

      set(state => ({
        eventTypes: state.eventTypes.map(et =>
          et.id === id ? { ...et, is_active: active } : et
        ),
      }));
    } catch (err: any) {
      set({ eventTypesError: err.message });
    }
  },

  // ══════════════════════════════════════════════════════════
  // AVAILABILITY
  // ══════════════════════════════════════════════════════════

  fetchAvailability: async (eventTypeId) => {
    if (!eventTypeId) return;
    set({ availabilityLoading: true });
    try {
      const userData = await getUserData();

      const { data, error } = await supabase
        .from('booking_availability')
        .select('*')
        .eq('organization_id', userData.organizationId)
        .eq('event_type_id', eventTypeId)
        .order('day_of_week', { ascending: true })
        .order('start_time', { ascending: true });

      if (error) throw error;
      set({ availability: data || [], availabilityLoading: false });
    } catch (err: any) {
      set({ availabilityLoading: false });
    }
  },

  saveAvailability: async (rules, eventTypeId, targetUserId?) => {
    if (!eventTypeId) return;
    try {
      const userData = await getUserData();
      const userId = targetUserId || userData.userId;

      // Delete existing rules for this event type + user
      let deleteQuery = supabase
        .from('booking_availability')
        .delete()
        .eq('organization_id', userData.organizationId)
        .eq('event_type_id', eventTypeId)
        .eq('user_id', userId);

      await deleteQuery;

      // Insert new rules
      if (rules.length > 0) {
        const rows = rules.map(r => ({
          organization_id: userData.organizationId,
          user_id: userId,
          event_type_id: eventTypeId,
          day_of_week: r.day_of_week,
          start_time: r.start_time,
          end_time: r.end_time,
          is_active: r.is_active ?? true,
          timezone: r.timezone || 'Europe/Rome',
        }));

        const { error } = await supabase.from('booking_availability').insert(rows);
        if (error) throw error;
      }

      await get().fetchAvailability(eventTypeId);
    } catch (err: any) {
      console.error('saveAvailability error:', err);
    }
  },

  fetchDateOverrides: async (eventTypeId) => {
    if (!eventTypeId) return;
    try {
      const userData = await getUserData();

      const { data, error } = await supabase
        .from('booking_date_overrides')
        .select('*, windows:booking_date_override_windows(*)')
        .eq('organization_id', userData.organizationId)
        .eq('event_type_id', eventTypeId)
        .order('override_date', { ascending: true });

      if (error) throw error;
      set({ dateOverrides: data || [] });
    } catch (err: any) {
      console.error('fetchDateOverrides error:', err);
    }
  },

  createDateOverride: async (data) => {
    try {
      const { organizationId, userId } = await getUserData();

      const { windows, ...overrideData } = data;

      const { data: created, error } = await supabase
        .from('booking_date_overrides')
        .insert({
          ...overrideData,
          organization_id: organizationId,
          user_id: userId,
          event_type_id: data.event_type_id,
        })
        .select()
        .single();

      if (error) throw error;

      // Add custom windows if not blocked
      if (!data.is_blocked && windows?.length && created) {
        const windowRows = windows.map(w => ({
          override_id: created.id,
          start_time: w.start_time,
          end_time: w.end_time,
        }));
        await supabase.from('booking_date_override_windows').insert(windowRows);
      }

      await get().fetchDateOverrides(data.event_type_id);
    } catch (err: any) {
      console.error('createDateOverride error:', err);
    }
  },

  deleteDateOverride: async (id) => {
    try {
      const { error } = await supabase
        .from('booking_date_overrides')
        .delete()
        .eq('id', id);

      if (error) throw error;
      set(state => ({
        dateOverrides: state.dateOverrides.filter(o => o.id !== id),
      }));
    } catch (err: any) {
      console.error('deleteDateOverride error:', err);
    }
  },

  // ══════════════════════════════════════════════════════════
  // BOOKINGS
  // ══════════════════════════════════════════════════════════

  fetchBookings: async (filters) => {
    set({ bookingsLoading: true, bookingsError: null });
    try {
      const { organizationId } = await getUserData();

      let query = supabase
        .from('bookings')
        .select(`
          *,
          event_type:booking_event_types!bookings_event_type_id_fkey(id, title, slug, duration_minutes, color),
          assigned_user:user_profiles!bookings_assigned_to_fkey(id, email, full_name, avatar_url),
          client:clients!bookings_client_id_fkey(id, business_name, email)
        `)
        .eq('organization_id', organizationId)
        .order('start_time', { ascending: false });

      if (filters?.status) {
        if (filters.status === 'upcoming') {
          query = query.in('status', ['confirmed', 'pending']).gte('start_time', new Date().toISOString());
          query = query.order('start_time', { ascending: true });
        } else if (filters.status === 'past') {
          query = query.in('status', ['confirmed', 'completed', 'no_show']).lt('start_time', new Date().toISOString());
        } else {
          query = query.eq('status', filters.status);
        }
      }

      if (filters?.from) {
        query = query.gte('start_time', filters.from);
      }
      if (filters?.to) {
        query = query.lte('start_time', filters.to);
      }

      // Filtra per utente assegnato (visibilità personale)
      if (filters?.filterByUserId) {
        query = query.eq('assigned_to', filters.filterByUserId);
      }

      const { data, error } = await query.limit(200);
      if (error) throw error;
      set({ bookings: data || [], bookingsLoading: false });
    } catch (err: any) {
      set({ bookingsError: err.message, bookingsLoading: false });
    }
  },

  fetchBookingById: async (id) => {
    try {
      const { organizationId } = await getUserData();

      const { data, error } = await supabase
        .from('bookings')
        .select(`
          *,
          event_type:booking_event_types!bookings_event_type_id_fkey(id, title, slug, duration_minutes, color),
          assigned_user:user_profiles!bookings_assigned_to_fkey(id, email, full_name, avatar_url),
          client:clients!bookings_client_id_fkey(id, business_name, email)
        `)
        .eq('id', id)
        .eq('organization_id', organizationId)
        .single();

      if (error) throw error;
      return data;
    } catch (err: any) {
      return null;
    }
  },

  cancelBooking: async (id, reason) => {
    try {
      const { data, error } = await supabase.functions.invoke('booking-manage', {
        body: { booking_id: id, operation: 'cancel', cancel_reason: reason },
      });
      if (error) throw error;
      set(state => ({
        bookings: state.bookings.map(b => b.id === id ? { ...b, status: 'cancelled' as const } : b),
      }));
      return true;
    } catch (err) {
      return false;
    }
  },

  rescheduleBooking: async (id, newStartTime, newEndTime, forceOverride) => {
    try {
      const { data, error } = await supabase.functions.invoke('booking-manage', {
        body: { booking_id: id, operation: 'reschedule', new_start_time: newStartTime, new_end_time: newEndTime || undefined, force_override: forceOverride || false },
      });
      // booking-manage ritorna HTTP 409 con { error:'slot_taken' } se lo slot è occupato:
      // supabase-js lo espone come FunctionsHttpError col body in error.context (Response).
      if (error) {
        const body = await parseFunctionsError(error);
        if (body) return { success: false, error: mapBookingError(body) };
        throw error;
      }
      // difensivo: alcune risposte possono arrivare 200 con { success:false }
      if (data && data.success === false) {
        return { success: false, error: mapBookingError(data) };
      }
      await get().fetchBookings();
      return { success: true };
    } catch (err: any) {
      return { success: false, error: err?.message || 'Riprogrammazione non riuscita' };
    }
  },

  completeBooking: async (id) => {
    try {
      const { error } = await supabase.functions.invoke('booking-manage', {
        body: { booking_id: id, operation: 'complete' },
      });
      if (error) throw error;
      set(state => ({
        bookings: state.bookings.map(b => b.id === id ? { ...b, status: 'completed' as const } : b),
      }));
      return true;
    } catch (err) {
      return false;
    }
  },

  markNoShow: async (id) => {
    try {
      const { error } = await supabase.functions.invoke('booking-manage', {
        body: { booking_id: id, operation: 'no_show' },
      });
      if (error) throw error;
      set(state => ({
        bookings: state.bookings.map(b => b.id === id ? { ...b, status: 'no_show' as const } : b),
      }));
      return true;
    } catch (err) {
      return false;
    }
  },

  confirmBooking: async (id) => {
    try {
      const { error } = await supabase.functions.invoke('booking-manage', {
        body: { booking_id: id, operation: 'confirm' },
      });
      if (error) throw error;
      set(state => ({
        bookings: state.bookings.map(b => b.id === id ? { ...b, status: 'confirmed' as const } : b),
      }));
      return true;
    } catch (err) {
      return false;
    }
  },

  rejectBooking: async (id, reason) => {
    try {
      const { error } = await supabase.functions.invoke('booking-manage', {
        body: { booking_id: id, operation: 'reject', cancel_reason: reason },
      });
      if (error) throw error;
      set(state => ({
        bookings: state.bookings.map(b => b.id === id ? { ...b, status: 'cancelled' as const } : b),
      }));
      return true;
    } catch (err) {
      return false;
    }
  },

  updateBookingNotes: async (id, notes) => {
    try {
      const { error } = await supabase
        .from('bookings')
        .update({ internal_notes: notes })
        .eq('id', id);
      if (error) throw error;
      set(state => ({
        bookings: state.bookings.map(b => b.id === id ? { ...b, internal_notes: notes } : b),
      }));
      return true;
    } catch (err) {
      return false;
    }
  },

  createInternalBooking: async (data) => {
    try {
      const { userId, organizationId } = await getUserData();

      // Fetch event type per calcolare end_time
      const { data: eventType, error: etError } = await supabase
        .from('booking_event_types')
        .select('duration_minutes')
        .eq('id', data.event_type_id)
        .single();

      if (etError || !eventType) throw new Error('Event type not found');

      const startDate = new Date(data.start_time);
      // end_time esplicito (durata manuale) ha priorità; altrimenti calcola dalla durata dell'event type
      const endDate = data.end_time
        ? new Date(data.end_time)
        : new Date(startDate.getTime() + eventType.duration_minutes * 60 * 1000);

      // Auto-link client per email
      let clientId = data.client_id || null;
      if (!clientId && data.booker_email) {
        const { data: existingClient } = await supabase
          .from('clients')
          .select('id')
          .eq('organization_id', organizationId)
          .eq('email', data.booker_email)
          .limit(1)
          .single();
        if (existingClient) clientId = existingClient.id;
      }

      const { data: booking, error } = await supabase
        .from('bookings')
        .insert({
          organization_id: organizationId,
          event_type_id: data.event_type_id,
          assigned_to: data.assigned_to || userId,
          start_time: startDate.toISOString(),
          end_time: endDate.toISOString(),
          booker_name: data.booker_name,
          booker_email: data.booker_email,
          booker_phone: data.booker_phone || null,
          booker_notes: data.booker_notes || null,
          booker_timezone: data.booker_timezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
          custom_field_answers: data.custom_field_answers || {},
          status: 'pending',
          public_token: crypto.randomUUID(),
          client_id: clientId,
          internal_notes: data.internal_notes || null,
        })
        .select()
        .single();

      if (error) throw error;

      // Confirm via edge function — handles email, Google Calendar, Meet link, notifications
      if (booking) {
        await supabase.functions.invoke('booking-manage', {
          body: { booking_id: booking.id, operation: 'confirm' },
        });

        // Emit workflow trigger event
        try {
          eventBus.emitEvent('booking:created' as any, {
            bookingId: booking.id,
            clientId: booking.client_id,
            startTime: booking.start_time,
            bookerName: booking.booker_name,
            bookerEmail: booking.booker_email,
            eventTypeId: booking.event_type_id,
            status: booking.status,
          });
        } catch (e) { console.warn('[BookingStore] Failed to emit booking:created event:', e); }
      }

      return booking;
    } catch (err) {
      console.error('Error creating internal booking:', err);
      return null;
    }
  },

  // ══════════════════════════════════════════════════════════
  // SETTINGS
  // ══════════════════════════════════════════════════════════

  fetchBookingSettings: async () => {
    set({ settingsLoading: true });
    try {
      const { organizationId } = await getUserData();

      const { data, error } = await supabase
        .from('booking_settings')
        .select('*')
        .eq('organization_id', organizationId)
        .single();

      if (error && error.code !== 'PGRST116') throw error; // PGRST116 = no rows
      set({ bookingSettings: data || null, settingsLoading: false });
    } catch (err: any) {
      set({ settingsLoading: false });
    }
  },

  saveBookingSettings: async (data) => {
    try {
      const { organizationId } = await getUserData();

      const existing = get().bookingSettings;

      if (existing) {
        const { error } = await supabase
          .from('booking_settings')
          .update(data)
          .eq('organization_id', organizationId);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('booking_settings')
          .insert({ ...data, organization_id: organizationId });
        if (error) throw error;
      }

      await get().fetchBookingSettings();
    } catch (err: any) {
      console.error('saveBookingSettings error:', err);
    }
  },

  // ══════════════════════════════════════════════════════════
  // USER BOOKING SLUG
  // ══════════════════════════════════════════════════════════

  fetchUserBookingSlug: async () => {
    try {
      const { bookingSlug } = await getUserData();
      set({ userBookingSlug: bookingSlug });
    } catch (err: any) {
      console.error('fetchUserBookingSlug error:', err);
    }
  },

  saveUserBookingSlug: async (slug) => {
    try {
      const { userId } = await getUserData();

      const { error } = await supabase
        .from('user_profiles')
        .update({ booking_slug: slug })
        .eq('id', userId);

      if (error) throw error;
      set({ userBookingSlug: slug });
      return true;
    } catch (err: any) {
      console.error('saveUserBookingSlug error:', err);
      return false;
    }
  },

  // ══════════════════════════════════════════════════════════
  // PUBLIC ACTIONS (no auth - for booking page)
  // ══════════════════════════════════════════════════════════

  fetchPublicPage: async (userSlug) => {
    set({ publicError: null });
    try {
      const response = await supabase.functions.invoke('booking-get-page', {
        body: { user_slug: userSlug },
      });
      if (response.error) throw response.error;
      set({ publicOrgData: response.data });
    } catch (err: any) {
      set({ publicError: err.message });
    }
  },

  fetchPublicEventType: async (userSlug, eventTypeSlug) => {
    set({ publicError: null });
    try {
      const response = await supabase.functions.invoke('booking-get-page', {
        body: { user_slug: userSlug, event_type_slug: eventTypeSlug },
      });
      if (response.error) throw response.error;
      set({ publicEventTypeData: response.data });
    } catch (err: any) {
      set({ publicError: err.message });
    }
  },

  fetchPublicSlots: async (eventTypeId, date, timezone, userId?) => {
    set({ publicSlotsLoading: true, publicError: null });
    try {
      const body: any = { event_type_id: eventTypeId, date, timezone };
      if (userId) body.user_id = userId;
      const response = await supabase.functions.invoke('booking-get-slots', {
        body,
      });
      if (response.error) throw response.error;
      set({ publicSlots: response.data?.slots || [], publicAllSlots: response.data?.allSlots || response.data?.slots || [], publicBusyIntervals: response.data?.busyIntervals || [], publicDayBookings: response.data?.dayBookings || [], publicSlotsLoading: false });
    } catch (err: any) {
      set({ publicSlots: [], publicAllSlots: [], publicBusyIntervals: [], publicDayBookings: [], publicSlotsLoading: false, publicError: err.message });
    }
  },

  createPublicBooking: async (userSlug, eventTypeSlug, data) => {
    set({ publicBookingCreating: true, publicError: null });
    try {
      const response = await supabase.functions.invoke('booking-create', {
        body: {
          user_slug: userSlug,
          event_type_slug: eventTypeSlug,
          ...data,
        },
      });
      if (response.error) {
        const body = await parseFunctionsError(response.error);
        throw new Error(mapPublicBookingError(body));
      }
      if (!response.data?.success) {
        throw new Error(mapPublicBookingError(response.data));
      }
      set({ publicBookingResult: response.data.booking, publicBookingCreating: false });
      return response.data.booking;
    } catch (err: any) {
      set({ publicError: err.message, publicBookingCreating: false });
      return null;
    }
  },

  resetPublicFlow: () => {
    set({
      publicOrgData: null,
      publicEventTypeData: null,
      publicSlots: [],
      publicAllSlots: [],
      publicBusyIntervals: [],
      publicDayBookings: [],
      publicSlotsLoading: false,
      publicBookingResult: null,
      publicBookingCreating: false,
      publicError: null,
    });
  },

  // ══════════════════════════════════════════════════════════
  // PUBLIC SELF-SERVICE (manage via public_token)
  // ══════════════════════════════════════════════════════════

  fetchPublicBooking: async (publicToken) => {
    set({ publicManagedLoading: true, publicManagedError: null, publicManagedSuccess: null });
    try {
      const response = await supabase.functions.invoke('booking-manage', {
        body: { public_token: publicToken, operation: 'get' },
      });
      if (response.error) throw response.error;
      if (!response.data?.success) throw new Error(response.data?.error || 'Booking not found');
      set({ publicManagedBooking: response.data.booking, publicManagedLoading: false });
    } catch (err: any) {
      set({ publicManagedError: err.message, publicManagedLoading: false });
    }
  },

  cancelPublicBooking: async (publicToken, reason) => {
    set({ publicManagedError: null, publicManagedSuccess: null });
    try {
      const response = await supabase.functions.invoke('booking-manage', {
        body: { public_token: publicToken, operation: 'cancel', cancel_reason: reason },
      });
      if (response.error) throw response.error;
      if (!response.data?.success) throw new Error(response.data?.error || 'Cancel failed');
      set((state) => ({
        publicManagedBooking: state.publicManagedBooking
          ? { ...state.publicManagedBooking, status: 'cancelled' }
          : null,
        publicManagedSuccess: 'Prenotazione cancellata con successo',
      }));
      return true;
    } catch (err: any) {
      set({ publicManagedError: err.message });
      return false;
    }
  },

  reschedulePublicBooking: async (publicToken, newStartTime) => {
    set({ publicManagedError: null, publicManagedSuccess: null });
    try {
      const response = await supabase.functions.invoke('booking-manage', {
        body: { public_token: publicToken, operation: 'reschedule', new_start_time: newStartTime },
      });
      if (response.error) throw response.error;
      if (!response.data?.success) {
        throw new Error(
          response.data?.error === 'slot_taken'
            ? 'Lo slot selezionato non è più disponibile'
            : response.data?.error || 'Reschedule failed'
        );
      }
      set({
        publicManagedBooking: {
          ...get().publicManagedBooking,
          status: 'rescheduled',
        },
        publicManagedSuccess: 'Prenotazione riprogrammata con successo',
      });
      return true;
    } catch (err: any) {
      set({ publicManagedError: err.message });
      return false;
    }
  },

  resetPublicManaged: () => {
    set({
      publicManagedBooking: null,
      publicManagedLoading: false,
      publicManagedError: null,
      publicManagedSuccess: null,
    });
  },
}));
